import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";

import {
  Search as SearchIcon,
  Edit as EditIcon,
  TrackChanges as TrackIcon,
} from "@mui/icons-material";
import AssessmentcontrolApiService from "../../services/AssessmentcontrolApiService";

const AssessorAllotment = () => {
  const navigate = useNavigate();

  const [applicationNo, setApplicationNo] = useState("");
  const [applicationType, setApplicationType] = useState("");
  const [appTypeOptions, setAppTypeOptions] = useState([]);
  const [appNoConfirmed, setAppNoConfirmed] = useState(false);
  const [applicationData, setApplicationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [editData, setEditData] = useState({
    capacity: "",
    DateFrom: "",
    DateTo: "",
    Category: "",
    LocationBypass: 0,
    equipmentBypass: 0,
    applicationBypass: 0,
    checklistBypass: 0,
    remarks: ""
  });

  const handleEditClick = (row) => {
    setSelectedRow(row);
    setEditData({
      capacity: row.capacity || "",
      DateFrom: row.DateFrom || "",
      DateTo: row.DateTo || "",
      Category: row.Category || "",
      LocationBypass: row.Location ?? 0,
      equipmentBypass: row.isEquipmentBypass ?? 0,
      applicationBypass: row.isApplicationBypass ?? 0,
      checklistBypass: row.isChecklistBypass ?? 0,
      remarks: ""
    });
    setOpenEdit(true);
  };

  const handleChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const resetEditState = () => {
    setEditData({
      capacity: "",
      DateFrom: "",
      DateTo: "",
      Category: "",
      LocationBypass: 0,
      equipmentBypass: 0,
      applicationBypass: 0,
      checklistBypass: 0,
      remarks: ""
    });
    setSelectedRow(null);
    setOpenEdit(false);
  };

  const handleSave = async () => {
    if (!editData.remarks || editData.remarks.trim() === "") {
      alert("Remarks is mandatory for updating assessor details");
      return;
    }

    try {
      const payload = {
        AppId: selectedRow.AppId,
        OrgId: localStorage.getItem('orgId'),
        AssessorAllotmentId: selectedRow.AssessorAllotmentId,
        AllotedToId: selectedRow.AllotedToId,
        Category: editData.Category,
        DateFrom: editData.DateFrom,
        DateTo: editData.DateTo,
        LocationBypass: editData.LocationBypass,
        equipmentBypass: editData.equipmentBypass,
        applicationBypass: editData.applicationBypass,
        checklistBypass: editData.checklistBypass,
        remarks: editData.remarks
      };

      const result = await AssessmentcontrolApiService.updateAssessorDetails(payload);

      if (result.success) {
        await handleSearch();
        setSuccessMessage(result.message || "Updated Successfully");
        setOpenSnackbar(true);
        resetEditState();
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update assessor details");
    }
  };

  const handleAppNoSearch = async () => {
    if (!applicationNo.trim()) {
      setError("Enter Application Number");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setAppNoConfirmed(false);
      setApplicationType("");
      setAppTypeOptions([]);
      setApplicationData([]);

      const result = await AssessmentcontrolApiService.getApplicationTypeByAppNo(applicationNo.trim());
      if (result.success && result.data.length > 0) {
        setAppTypeOptions(result.data);
        setAppNoConfirmed(true);
      } else {
        setError("Application not found");
      }
    } catch (err) {
      setError("Application not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!applicationNo || !applicationType) {
      setError("Enter Application Number and select Application Type");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await AssessmentcontrolApiService.getCABDetails({
        applicationno: applicationNo,
        applicationType
      });

      if (result.data && result.data.length > 0) {
        setApplicationData(result.data);
      } else {
        setApplicationData([]);
        setError("Application not found");
      }
    } catch (err) {
      setError("Application not found");
      setApplicationData([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "LA":       return "secondary";
      case "TL":       return "primary";
      case "LATA":     return "warning";
      case "TA":       return "success";
      case "Observer": return "info";
      case "TE":       return "error";
      default:         return "default";
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh", py: 2 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Step 1: Search */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Step 1: Search Application
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              label="Application No"
              value={applicationNo}
              onChange={(e) => {
                setApplicationNo(e.target.value);
                setAppNoConfirmed(false);
                setApplicationType("");
                setAppTypeOptions([]);
                setApplicationData([]);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && applicationNo.trim()) handleAppNoSearch();
              }}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleAppNoSearch}
              disabled={loading}
              sx={{ bgcolor: "#00024c", "&:hover": { bgcolor: "#000380" }, height: 40 }}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {appNoConfirmed && appTypeOptions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Application Type</InputLabel>
              <Select
                value={applicationType}
                label="Select Application Type"
                onChange={(e) => {
                  setApplicationType(e.target.value);
                  setApplicationData([]);
                  setError("");
                }}
              >
                <MenuItem value="">-- Select --</MenuItem>
                {appTypeOptions.map((opt) => (
                  <MenuItem key={opt.AppId} value={opt.ApplicationTypeName}>
                    {opt.ApplicationTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {applicationType && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ bgcolor: "#E34124", "&:hover": { bgcolor: "#c73518" }, height: 40, px: 3 }}
            >
              Load Details
            </Button>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {/* Application Summary */}
      {applicationData.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Application No</Typography>
                <Typography variant="h6" fontWeight={600}>{applicationData[0].ApplicationNo}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Stage</Typography>
                <Chip label={applicationData[0].Stage} color="info" sx={{ mt: 0.5 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Total Assessors</Typography>
                <Typography variant="h6" fontWeight={600}>{applicationData.length}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Assessor Table */}
      {applicationData.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" mb={2} fontWeight={600}>
              Assessor Allotment Details
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr.No/Action</TableCell>
                    <TableCell>Assessor Name</TableCell>
                    <TableCell>Login</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Date From</TableCell>
                    <TableCell>Date To</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {applicationData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{index + 1}</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditClick(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<TrackIcon />}
                            onClick={() =>
                              navigate(
                                `/main/admin/assessment-tracking-log?appId=${encodeURIComponent(row.AppId || "")}&allotedToId=${encodeURIComponent(row.AllotedToId || "")}`
                              )
                            }
                            sx={{
                              backgroundColor: "#0b3b8c",
                              "&:hover": { backgroundColor: "#082d6e" },
                              textTransform: "none",
                              fontSize: "0.7rem",
                            }}
                          >
                            Tracking Log
                          </Button>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={500}>{row.assessorName}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip label={row.assessorLogin} size="small" variant="outlined" />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.Category}
                          color={getCategoryColor(row.Category)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 250 }}>{row.disciplineName}</TableCell>

                      <TableCell>
                        {row.DateFrom ? new Date(row.DateFrom).toLocaleDateString() : ""}
                      </TableCell>

                      <TableCell>
                        {row.DateTo ? new Date(row.DateTo).toLocaleDateString() : ""}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.Location === 1 ? "Yes" : "No"}
                          color={row.Location === 1 ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Assessor Details</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Category"
                name="Category"
                select
                value={editData.Category}
                onChange={handleChange}
              >
                <MenuItem value="LA">LA</MenuItem>
                <MenuItem value="TL">TL</MenuItem>
                <MenuItem value="LATA">LATA</MenuItem>
                <MenuItem value="TA">TA</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="date"
                fullWidth
                label="Date From"
                name="DateFrom"
                value={editData.DateFrom?.slice(0, 10)}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="date"
                fullWidth
                label="Date To"
                name="DateTo"
                value={editData.DateTo?.slice(0, 10)}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Location Bypass"
                name="LocationBypass"
                select
                value={editData.LocationBypass ?? 0}
                onChange={handleChange}
              >
                <MenuItem value={1}>Yes</MenuItem>
                <MenuItem value={0}>No</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Equipment Bypass"
                name="equipmentBypass"
                select
                value={editData.equipmentBypass ?? 0}
                onChange={handleChange}
              >
                <MenuItem value={1}>Yes</MenuItem>
                <MenuItem value={0}>No</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Application Bypass"
                name="applicationBypass"
                select
                value={editData.applicationBypass ?? 0}
                onChange={handleChange}
              >
                <MenuItem value={1}>Yes</MenuItem>
                <MenuItem value={0}>No</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Checklist Bypass"
                name="checklistBypass"
                select
                value={editData.checklistBypass ?? 0}
                onChange={handleChange}
              >
                <MenuItem value={1}>Yes</MenuItem>
                <MenuItem value={0}>No</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                value={editData.remarks}
                onChange={handleChange}
                required
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={resetEditState}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessorAllotment;